// /docs and /docs/:slug — the human-readable documentation site.
//
// ONE SOURCE OF TRUTH: the repo's markdown. `npm run build:docs` publishes it
// to public/docs/*.md + public/docs/index.json, plus /llms.txt and
// /llms-full.txt for agents (llmstxt.org). This route just fetches and renders
// those same files — so humans and agents literally read the same bytes and
// the docs can never drift between the two audiences.

import {
	Alert,
	Anchor,
	Badge,
	Card,
	Code,
	Group,
	Loader,
	NavLink,
	Stack,
	Table,
	Text,
	Title,
	TypographyStylesProvider,
} from "@mantine/core";
import { IconFileText, IconRobot } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface DocEntry {
	slug: string;
	title: string;
	audience: string;
	summary: string;
	bytes: number;
}

const AUDIENCE_LABEL: Record<string, string> = {
	start: "Start here",
	reference: "Reference",
	operate: "Operating the platform",
};

function useIndex() {
	const [docs, setDocs] = useState<DocEntry[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	useEffect(() => {
		fetch("/docs/index.json")
			.then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
			.then(setDocs)
			.catch((e) => setError(e instanceof Error ? e.message : String(e)));
	}, []);
	return { docs, error };
}

/** Shared markdown renderer — GFM tables, Mantine typography, links that stay
 *  inside the SPA when they point at other docs. */
function Markdown({ source }: { source: string }) {
	return (
		<TypographyStylesProvider>
			<ReactMarkdown
				remarkPlugins={[remarkGfm]}
				components={{
					table: ({ children }) => (
						<Table striped withTableBorder withColumnBorders>
							{children}
						</Table>
					),
					a: ({ href, children }) => {
						const internal = href?.startsWith("/docs/");
						const slug = internal ? href?.replace("/docs/", "").replace(/\.md$/, "") : null;
						return slug ? (
							<Anchor component={Link} to={`/docs/${slug}`}>
								{children}
							</Anchor>
						) : (
							<Anchor href={href} target={href?.startsWith("http") ? "_blank" : undefined}>
								{children}
							</Anchor>
						);
					},
				}}
			>
				{source}
			</ReactMarkdown>
		</TypographyStylesProvider>
	);
}

export function DocsIndex() {
	const { docs, error } = useIndex();

	if (error) {
		return (
			<Alert color="red" variant="light" title="Couldn't load the docs index">
				{error}
			</Alert>
		);
	}
	if (!docs) {
		return (
			<Group justify="center" mt={80}>
				<Loader />
			</Group>
		);
	}

	const groups = Object.keys(AUDIENCE_LABEL).filter((a) => docs.some((d) => d.audience === a));

	return (
		<Stack gap="xl" maw={860}>
			<Stack gap={4}>
				<Title order={1}>Documentation</Title>
				<Text c="dimmed">
					Everything about building on s3p. The same markdown powers this site, the{" "}
					<Code>/skills</Code> guide agents read, and the machine endpoints below.
				</Text>
			</Stack>

			<Card withBorder radius="md" padding="md" bg="var(--mantine-color-default-hover)">
				<Group gap="xs" mb={6}>
					<IconRobot size={18} />
					<Text fw={600} size="sm">
						Using an AI agent?
					</Text>
				</Group>
				<Text size="sm" c="dimmed">
					This site follows the <Anchor href="https://llmstxt.org" target="_blank">llms.txt</Anchor>{" "}
					convention. Point your agent at{" "}
					<Anchor href="/llms.txt">
						<Code>/llms.txt</Code>
					</Anchor>{" "}
					for a curated map, or{" "}
					<Anchor href="/llms-full.txt">
						<Code>/llms-full.txt</Code>
					</Anchor>{" "}
					to ingest every document in one fetch. Each page is also plain markdown at{" "}
					<Code>/docs/&lt;slug&gt;.md</Code>.
				</Text>
			</Card>

			{groups.map((audience) => (
				<Stack key={audience} gap="sm">
					<Title order={3}>{AUDIENCE_LABEL[audience]}</Title>
					{docs
						.filter((d) => d.audience === audience)
						.map((d) => (
							<Card key={d.slug} withBorder radius="md" padding="md" component={Link} to={`/docs/${d.slug}`}>
								<Group justify="space-between" wrap="nowrap">
									<Stack gap={2}>
										<Group gap="xs">
											<IconFileText size={16} />
											<Text fw={600}>{d.title}</Text>
										</Group>
										<Text size="sm" c="dimmed">
											{d.summary}
										</Text>
									</Stack>
									<Badge variant="light" size="sm">
										{Math.max(1, Math.round(d.bytes / 1024))} KB
									</Badge>
								</Group>
							</Card>
						))}
				</Stack>
			))}
		</Stack>
	);
}

export function DocPage() {
	const { slug } = useParams<{ slug: string }>();
	const { docs } = useIndex();
	const [source, setSource] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!slug) return;
		setSource(null);
		setError(null);
		fetch(`/docs/${slug}.md`)
			.then((r) => (r.ok ? r.text() : Promise.reject(new Error(`HTTP ${r.status}`))))
			.then((t) => {
				// A missing doc falls back to the SPA shell — detect and report it
				// rather than rendering index.html as prose.
				if (t.trimStart().startsWith("<")) throw new Error("not found");
				setSource(t);
			})
			.catch((e) => setError(e instanceof Error ? e.message : String(e)));
	}, [slug]);

	const meta = docs?.find((d) => d.slug === slug);

	return (
		<Group align="flex-start" gap="xl" wrap="nowrap">
			{docs && (
				<Stack gap={2} w={220} visibleFrom="md">
					<Text size="xs" tt="uppercase" c="dimmed" fw={700} mb={4}>
						Docs
					</Text>
					{docs.map((d) => (
						<NavLink
							key={d.slug}
							component={Link}
							to={`/docs/${d.slug}`}
							label={d.title}
							active={d.slug === slug}
						/>
					))}
				</Stack>
			)}

			<Stack gap="md" maw={820} flex={1}>
				{error ? (
					<Alert color="red" variant="light" title="Couldn't load this document">
						{error} — <Anchor component={Link} to="/docs">back to the index</Anchor>
					</Alert>
				) : !source ? (
					<Group justify="center" mt={80}>
						<Loader />
					</Group>
				) : (
					<>
						<Group justify="space-between">
							<Anchor component={Link} to="/docs" size="sm">
								← All docs
							</Anchor>
							<Anchor href={`/docs/${slug}.md`} size="sm" c="dimmed">
								View as markdown ↗
							</Anchor>
						</Group>
						{meta && (
							<Text size="sm" c="dimmed">
								{meta.summary}
							</Text>
						)}
						<Markdown source={source} />
					</>
				)}
			</Stack>
		</Group>
	);
}
